<template>
  <section class="account-email-subscriptions" aria-labelledby="heading-subscription">
    <div class="email-subscriptions-header">
      <h2 id="heading-subscription" class="text-h1 text-textDarkest mb-0">
        Email Subscriptions
      </h2>
      <p class="text-body-1 text-textDark mb-0 mt-1">
        Receive automated report emails delivered to {{ email || 'your email' }}.
      </p>
    </div>

    <div class="email-subscription-rows">
      <div
        v-for="report in reports"
        :key="report.key"
        class="email-subscription-row"
      >
        <span class="text-body-1 text-textDarkest">
          {{ report.label }}
        </span>
        <FrequencySelector
          :model-value="frequencies[report.key]"
          :aria-label="`${report.label} frequency`"
          @update:model-value="setFrequency(report.key, $event)"
        />
      </div>
    </div>

    <DpplAutoSizedButton
      :disabled="!isDirty || isLoading"
      height="36"
      @click="handleSave"
    >
      Update Email Subscriptions
    </DpplAutoSizedButton>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import DpplAutoSizedButton from '@/components/datapeople/DpplAutoSizedButton.vue';
import FrequencySelector from '@/components/shared/FrequencySelector.vue';
import {
  fetchEmailSubscriptions,
  updateEmailSubscriptions,
} from '@/api/settings';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppLoading } from '@/stores/app/loading';

const reports = [
  { key: 'usage', label: 'Usage Report' },
  { key: 'compliance', label: 'Compliance Report' },
  { key: 'employer_brand', label: 'Employer Brand Report' },
  { key: 'job_performance', label: 'Job Performance Report' },
];

const defaultFrequencies = () => ({
  usage: 'none',
  compliance: 'none',
  employer_brand: 'none',
  job_performance: 'none',
});

const { setLoading } = useAppLoading();
const { addErrorSnackbar, addSuccessSnackbar } = useAppSnackbar();

const email = ref('');
const frequencies = ref(defaultFrequencies());
const savedFrequencies = ref(defaultFrequencies());
const isLoading = ref(false);

const isDirty = computed(() => (
  reports.some((report) => frequencies.value[report.key] !== savedFrequencies.value[report.key])
));

const setFrequency = (reportKey, value) => {
  frequencies.value = {
    ...frequencies.value,
    [reportKey]: value,
  };
};

const load = async () => {
  isLoading.value = true;

  try {
    const data = await fetchEmailSubscriptions();

    email.value = data.email || '';

    frequencies.value = {
      ...defaultFrequencies(),
      ...(data.frequencies || {}),
    };

    savedFrequencies.value = { ...frequencies.value };
  } catch (error) {
    addErrorSnackbar({
      text: `Email subscriptions - ${error.message}`,
    });

    throw error;
  } finally {
    isLoading.value = false;
  }
};

const handleSave = async () => {
  try {
    setLoading({ componentLoading: true });

    const data = await updateEmailSubscriptions({
      frequencies: frequencies.value,
    });

    email.value = data.email || email.value;

    frequencies.value = {
      ...defaultFrequencies(),
      ...(data.frequencies || {}),
    };

    savedFrequencies.value = { ...frequencies.value };

    addSuccessSnackbar({
      text: 'Your email subscriptions have been updated',
    });
  } catch (error) {
    addErrorSnackbar({
      text: `Email subscriptions - ${error.message}`,
    });

    throw error;
  } finally {
    setLoading({ componentLoading: false });
  }
};

onMounted(() => {
  load();
});
</script>

<style scoped lang="less">
.account-email-subscriptions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  margin-top: 24px;
  max-width: 547px;
}

.email-subscription-rows {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 510px;
}

.email-subscription-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 0;
}
</style>
